/**
 * Modal "Zaksięguj jako koszt" (kontrakt POST /external-costs/z-ksef).
 * Formularz wstepnie wypelniony: kategoria (sugestia_kategorii z regul), sklep/ogolny (sugestia albo biezacy sklep),
 * status (do zaplaty / zaplacony + data; gdy XML ma Zaplacono=1 - od razu "zaplacony" z data z faktury), termin (puste =
 * termin z XML FA(3), pokazany w podpowiedzi), uwagi. Blok "Płatność wg faktury": forma, termin, rachunek, bank
 * (kolumny z migracji 057 - ida do kosztu automatycznie). Korekta = informacja o kwocie ujemnej i powiazaniu z kosztem
 * faktury korygowanej. 409 KOSZT_ISTNIEJE -> komunikat z id istniejacego kosztu.
 */
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { externalCostsService } from '../../services/externalCostsService';
import {
  formatujKwote, formatujDate, dzisISO, komunikatBledu, nazwaSklepu, ZRODLA_SUGESTII, skrocNumerKsef, formatujRachunek, opisPlatnosci,
} from './format';

const pole = { width: '100%', padding: '6px 10px', fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border)', backgroundColor: 'var(--card-bg)', color: 'var(--text)', boxSizing: 'border-box' };
const etykieta = { display: 'block', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '4px' };
const btn = (aktywny) => ({
  padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px', cursor: 'pointer',
  border: `1px solid ${aktywny ? 'var(--success)' : 'var(--text-muted)'}`,
  background: aktywny ? 'var(--success)' : 'var(--card-bg)', color: aktywny ? '#fff' : 'var(--text-muted)',
});

export default function ZaksiegujModal({ wiersz, kategorie = [], sklepy = [], locationId = '', onZamknij, onZaksiegowano }) {
  const sug = wiersz?.sugestia_kategorii || {};
  const [categoryId, setCategoryId] = useState(sug.category_id ? String(sug.category_id) : '');
  const [locId, setLocId] = useState(sug.location_id ? String(sug.location_id) : (locationId ? String(locationId) : ''));
  const [status, setStatus] = useState(wiersz?.zaplacono ? 'zaplacony' : 'niezaplacony');
  const [dataPlatnosci, setDataPlatnosci] = useState(wiersz?.data_zaplaty || dzisISO());
  const [termin, setTermin] = useState('');
  const [uwagi, setUwagi] = useState('');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState(null);
  const [istniejacy, setIstniejacy] = useState(wiersz?.koszt_istniejacy || null);

  const korekta = Boolean(wiersz?.korekta || wiersz?.sugestia === 'korekta');
  const zablokowany = Boolean(istniejacy);
  const platnosc = opisPlatnosci(wiersz);
  const maPlatnosc = Boolean(wiersz?.forma_platnosci || wiersz?.termin_platnosci || wiersz?.zaplacono || wiersz?.rachunek_nr);

  const zapisz = async (e) => {
    e.preventDefault();
    if (!categoryId) { setBlad('Wybierz kategorię kosztu.'); return; }
    setWysylanie(true);
    setBlad(null);
    try {
      const body = { ksef_id: wiersz.id, category_id: Number(categoryId), location_id: locId ? Number(locId) : null, status };
      if (status === 'zaplacony' && dataPlatnosci) body.data_platnosci = dataPlatnosci;
      if (termin) body.termin_platnosci = termin;
      if (uwagi.trim()) body.uwagi = uwagi.trim();
      const odp = await externalCostsService.ksiegujZKsef(body);
      const koszt = odp?.data || odp;
      toast.success(`Zaksięgowano koszt #${koszt.id} (${formatujKwote(koszt.kwota_brutto)})`);
      (koszt.ostrzezenia || []).forEach(o => toast(o, { icon: '⚠️', duration: 7000 }));
      if (onZaksiegowano) onZaksiegowano(koszt, wiersz);
      if (onZamknij) onZamknij();
    } catch (err) {
      if (err?.isAuthError) return;
      if (err?.errorCode === 'KOSZT_ISTNIEJE' && err?.data?.istniejacy) setIstniejacy(err.data.istniejacy);
      setBlad(komunikatBledu(err, 'Nie udało się zaksięgować kosztu.'));
    } finally {
      setWysylanie(false);
    }
  };

  return (
    <div data-testid="ksieguj-modal" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', maxWidth: '640px', width: '94%', maxHeight: '92vh', overflow: 'auto', border: '1px solid var(--border)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600 }}>
          {korekta ? 'Zaksięguj korektę kosztu' : 'Zaksięguj jako koszt'}
        </h3>
        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '12px' }}>
          Faktura z KSeF <span title={wiersz?.ksef_numer || ''}>{skrocNumerKsef(wiersz?.ksef_numer)}</span> — kwoty, numer, NIP, data wystawienia i termin idą z KSeF; tutaj tylko kategoria, sklep i status.
        </div>

        {/* Dane faktury (tylko odczyt) */}
        <div data-testid="ksieguj-dane" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', padding: '10px 12px', background: 'var(--bg)', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Numer: </span><strong>{wiersz?.numer_faktury || '—'}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Data: </span>{formatujDate(wiersz?.data_wystawienia)}</div>
          <div style={{ gridColumn: '1 / -1' }}><span style={{ color: 'var(--text-muted)' }}>Sprzedawca: </span>{wiersz?.sprzedawca_nazwa || '—'} <span style={{ color: 'var(--text-muted)' }}>NIP {wiersz?.sprzedawca_nip || '—'}</span></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Netto: </span>{formatujKwote(wiersz?.netto)}</div>
          <div><span style={{ color: 'var(--text-muted)' }}>Brutto: </span><strong>{formatujKwote(wiersz?.brutto)}</strong></div>
          <div data-testid="ksieguj-platnosc" style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--border)', paddingTop: '6px', marginTop: '2px' }}>
            <span style={{ color: 'var(--text-muted)' }}>Płatność wg faktury: </span>
            {maPlatnosc ? (
              <>
                <strong>{platnosc.forma || 'forma nieznana'}</strong>
                {' · '}<span style={{ color: platnosc.kolor, fontWeight: 600 }}>{platnosc.etykieta}</span>
                {wiersz?.rachunek_nr && (
                  <div style={{ fontSize: '11px', marginTop: '2px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>rachunek </span>
                    <span style={{ fontFamily: 'monospace' }}>{wiersz.rachunek_formatowany || formatujRachunek(wiersz.rachunek_nr)}</span>
                    {wiersz.rachunek_bank ? ` (${wiersz.rachunek_bank})` : ''}
                  </div>
                )}
              </>
            ) : <span style={{ color: 'var(--text-muted)' }}>{wiersz?.ma_xml === false ? 'brak XML — bez danych o płatności' : 'brak danych o płatności w XML'}</span>}
          </div>
        </div>

        {korekta && (
          <div data-testid="ksieguj-korekta" style={{ fontSize: '11px', padding: '8px 10px', borderRadius: '6px', background: 'var(--orange-light)', color: 'var(--orange)', marginBottom: '12px' }}>
            Korekta: koszt zostanie zapisany z kwotami ujemnymi.{' '}
            {wiersz?.korygowana_koszt
              ? <>Powiązanie z kosztem #{wiersz.korygowana_koszt.id} ({wiersz.korygowana_koszt.numer_dokumentu || wiersz.korygowana_koszt.nazwa}).</>
              : <>Nie znaleziono kosztu faktury korygowanej{wiersz?.numer_korygowanej ? ` (${wiersz.numer_korygowanej})` : ''} — zapis bez powiązania.</>}
          </div>
        )}

        {istniejacy && (
          <div data-testid="ksieguj-istniejacy" style={{ fontSize: '11px', padding: '8px 10px', borderRadius: '6px', background: 'var(--danger-light)', color: 'var(--danger)', marginBottom: '12px' }}>
            Ta faktura jest już zaksięgowana jako koszt #{istniejacy.id}{istniejacy.status ? ` (${istniejacy.status})` : ''}. Nie dodajemy drugi raz.
          </div>
        )}

        <form onSubmit={zapisz}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={etykieta}>Kategoria *</label>
              <select data-testid="ksieguj-kategoria" value={categoryId} onChange={e => setCategoryId(e.target.value)} style={pole} required>
                <option value="">Wybierz kategorię</option>
                {kategorie.map(k => <option key={k.id} value={k.id}>{k.nazwa}</option>)}
              </select>
              {sug.category_id && (
                <div data-testid="ksieguj-sugestia" style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>
                  Podpowiedź: {sug.kategoria_nazwa} ({ZRODLA_SUGESTII[sug.zrodlo] || sug.zrodlo}{sug.uzyc ? `, użyta ${sug.uzyc}×` : ''})
                </div>
              )}
            </div>
            <div>
              <label style={etykieta}>Sklep</label>
              <select data-testid="ksieguj-sklep" value={locId} onChange={e => setLocId(e.target.value)} style={pole}>
                <option value="">Ogólne (centrala) — bez sklepu</option>
                {sklepy.map(s => <option key={s.id} value={s.id}>{s.nazwa}</option>)}
              </select>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>{nazwaSklepu(locId ? Number(locId) : null, sklepy)}</div>
            </div>
            <div>
              <label style={etykieta}>Status</label>
              <select data-testid="ksieguj-status" value={status} onChange={e => setStatus(e.target.value)} style={pole}>
                <option value="niezaplacony">Do zapłaty</option>
                <option value="zaplacony">{wiersz?.zaplacono ? `Zapłacony (wg faktury: ${wiersz.forma_platnosci || 'zapłacono'}${wiersz.data_zaplaty ? `, ${formatujDate(wiersz.data_zaplaty)}` : ''})` : 'Zapłacony (z konta firmowego, bez KW)'}</option>
                <option value="planowany">Planowany</option>
              </select>
            </div>
            {status === 'zaplacony' ? (
              <div>
                <label style={etykieta}>Data zapłaty</label>
                <input data-testid="ksieguj-data-platnosci" type="date" value={dataPlatnosci} onChange={e => setDataPlatnosci(e.target.value)} style={pole} />
              </div>
            ) : (
              <div>
                <label style={etykieta}>Termin płatności</label>
                <input data-testid="ksieguj-termin" type="date" value={termin} onChange={e => setTermin(e.target.value)} style={pole} />
                <div data-testid="ksieguj-termin-hint" style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px' }}>
                  {wiersz?.termin_platnosci ? `puste = termin z faktury: ${formatujDate(wiersz.termin_platnosci)}` : 'puste = termin z XML FA(3), jeśli faktura go ma'}
                </div>
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={etykieta}>Uwagi księgowe</label>
              <textarea data-testid="ksieguj-uwagi" value={uwagi} onChange={e => setUwagi(e.target.value)} style={{ ...pole, minHeight: '56px' }} placeholder="np. prąd za sierpień, sklep Leszno" />
            </div>
          </div>

          {blad && <div data-testid="ksieguj-blad" style={{ marginTop: '10px', fontSize: '11px', color: 'var(--danger)' }}>{blad}</div>}

          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button type="button" data-testid="ksieguj-anuluj" onClick={onZamknij} style={btn(false)}>Anuluj</button>
            <button type="submit" data-testid="ksieguj-zapisz" disabled={wysylanie || zablokowany} style={{ ...btn(true), opacity: (wysylanie || zablokowany) ? 0.6 : 1 }}>
              {wysylanie ? 'Księgowanie…' : (korekta ? 'Zaksięguj korektę' : 'Zaksięguj koszt')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
